import { apiFetchData } from 'apps/auth/helpers/api';
import { clearGarbageAuthData } from 'apps/auth/helpers/clearData';
import { addMetaTag } from 'apps/auth/helpers/DOM';
import { getLangCountryPrefix } from 'apps/auth/helpers/utils';

type MaybePromise<T> = void | Promise<T>;
type ActionFn<TArgs extends unknown[] = unknown[], TReturn = unknown> = (...args: TArgs) => MaybePromise<TReturn>;

type FormPayload = {
    name: string;
    fields: unknown;
    action: unknown;
};

type ApiResponse = {
    redirectTo?: string;
    pageTitle?: string;
    pageDescription?: string;
    pageKeywords?: string;
    messages?: unknown;
    cheque?: unknown;
    chatBlocked?: unknown;
    currentSiteSection?: unknown;
    forms?: Record<string, FormPayload>;
    paymentForm?: unknown;
    auth?: unknown;
    cards?: unknown;
    messageBodyMessages?: unknown;
    authPromoAction?: unknown;
    extra?: { paymentHistory?: unknown; billHistory?: unknown } & Record<string, unknown>;
};

export class UpdateReduxService {
    #lang: string | undefined;
    #togglePopup?: ActionFn<[boolean]>;
    #toggleModal?: ActionFn<[boolean, ...unknown[]]>;
    #toggleSpinner?: ActionFn<[boolean]>;
    #setCheque?: ActionFn<[unknown]>;
    #setExtra?: ActionFn<[unknown]>;
    #setHistory?: ActionFn<[unknown]>;
    #updateCurrentSiteSection?: ActionFn<[unknown]>;
    #setNewForm?: ActionFn<[string, unknown, unknown]>;
    #initCalculator?: ActionFn<[unknown]>;
    #updateAuth?: ActionFn<[unknown]>;
    #updateCards?: ActionFn<[unknown]>;
    #updateMessages?: ActionFn<[unknown]>;
    #updateAuthPromoBlock?: ActionFn<[unknown]>;
    #updatePageMessages?: ActionFn<[unknown]>;
    #updateJsMessages?: ActionFn<[unknown]>;
    #stateOptions?: unknown;
    #chatBlockStatus?: ActionFn<[unknown]>;

    constructor(lang?: string) {
        this.#lang = lang;
    }

    setMessageAction(updateJsMessages: ActionFn<[unknown]>, updatePageMessages: ActionFn<[unknown]>) {
        if (this.#updateJsMessages === undefined) {
            this.#updateJsMessages = updateJsMessages;
            this.#updatePageMessages = updatePageMessages;
        }
    }

    setChatAction(chatBlockStatus: ActionFn<[unknown]>) {
        if (this.#chatBlockStatus === undefined) {
            this.#chatBlockStatus = chatBlockStatus;
        }
    }

    setHistoryAction(setCheque: ActionFn<[unknown]>, setHistory: ActionFn<[unknown]>) {
        if (this.#setCheque === undefined) {
            this.#setCheque = setCheque;
        }
        if (this.#setHistory === undefined) {
            this.#setHistory = setHistory;
        }
    }

    setAuthAction(updateAuth: ActionFn<[unknown]>) {
        if (this.#updateAuth === undefined) {
            this.#updateAuth = updateAuth;
        }
    }

    setFormAction(setNewForm: ActionFn<[string, unknown, unknown]>) {
        if (this.#setNewForm === undefined) {
            this.#setNewForm = setNewForm;
        }
    }

    setCalculatorAction(initCalculator: ActionFn<[unknown]>) {
        if (this.#initCalculator === undefined) {
            this.#initCalculator = initCalculator;
        }
    }

    setAppAction(
        toggleModal: ActionFn<[boolean, ...unknown[]]>,
        togglePopup: ActionFn<[boolean]>,
        toggleSpinner: ActionFn<[boolean]>,
        updateAuthPromoBlock: ActionFn<[unknown]>,
        updateCurrentSiteSection: ActionFn<[unknown]>,
        updateMessages: ActionFn<[unknown]>,
    ) {
        if (this.#toggleModal === undefined) {
            this.#toggleModal = toggleModal;
            this.#togglePopup = togglePopup;
            this.#toggleSpinner = toggleSpinner;
            this.#updateAuthPromoBlock = updateAuthPromoBlock;
            this.#updateCurrentSiteSection = updateCurrentSiteSection;
            this.#updateMessages = updateMessages;
        }
    }

    setExtraAction(setExtra: ActionFn<[unknown]>) {
        if (this.#setExtra === undefined) {
            this.#setExtra = setExtra;
        }
    }

    setCardsAction(updateCards: ActionFn<[unknown]>) {
        if (this.#updateCards === undefined) {
            this.#updateCards = updateCards;
        }
    }

    setStateOptions(stateOptions: unknown) {
        this.#stateOptions = stateOptions;
    }

    fetchData(lang?: string, _isAuthZone?: boolean, country?: string) {
        void this.#togglePopup!(false);
        void this.#toggleModal!(false);
        void this.#toggleSpinner!(true);
        const apiUrl = this.#getUrl(lang, country);
        let isToggleSpinner = true;
        return apiFetchData<ApiResponse>(apiUrl)
            .then((json: ApiResponse) => {
                if (json.redirectTo) {
                    isToggleSpinner = false;
                    window.location.href = json.redirectTo;
                    return undefined;
                }
                this.#updateDocument(json);
                return this.#updateRedux(json);
            })
            .finally(() => {
                if (isToggleSpinner) {
                    void this.#toggleSpinner!(false);
                }
            });
    }

    #getUrl(lang?: string, country?: string) {
        let apiUrl = `${window.location.pathname}${window.location.search}`;
        if (lang && this.#lang && lang !== this.#lang) {
            if (apiUrl.indexOf(getLangCountryPrefix(this.#lang, country)) !== -1) {
                apiUrl = apiUrl.replace(getLangCountryPrefix(this.#lang, country), getLangCountryPrefix(lang, country));
            } else {
                apiUrl = apiUrl + (apiUrl.indexOf('?') === -1 ? '?' : '&') + 'lang=' + lang;
            }
            this.#lang = lang;
        }
        return apiUrl;
    }

    #updateDocument = (json: ApiResponse = {}) => {
        document.title = json.pageTitle ?? '';
        const description = document.querySelector('meta[name="description"]');
        if (description) {
            description.setAttribute('content', json.pageDescription ?? '');
        } else if (json.pageDescription) {
            addMetaTag('description', json.pageDescription);
        }
        const keywords = document.querySelector('meta[name="keywords"]');
        if (keywords) {
            keywords.setAttribute('content', json.pageKeywords ?? '');
        } else if (json.pageKeywords) {
            addMetaTag('keywords', json.pageKeywords);
        }
    };

    #updateRedux(json: ApiResponse = {}): Promise<ApiResponse> {
        const promises: Array<Promise<unknown> | void> = [];
        if (json.messages && this.#updatePageMessages) {
            promises.push(this.#updatePageMessages(json.messages));
        }
        if (json.cheque && this.#setCheque) {
            promises.push(this.#setCheque(json.cheque));
        }
        if (this.#chatBlockStatus) {
            promises.push(this.#chatBlockStatus(json.chatBlocked));
        }
        if (json.currentSiteSection && this.#updateCurrentSiteSection) {
            promises.push(this.#updateCurrentSiteSection(json.currentSiteSection));
        }
        if (json.forms && this.#setNewForm) {
            const setNewForm = this.#setNewForm;
            Object.values(json.forms).forEach((form) => {
                promises.push(setNewForm(form.name, form.fields, form.action));
            });
        }
        if (json.paymentForm && this.#initCalculator) {
            promises.push(this.#initCalculator(json));
        }
        if (json.auth && this.#updateAuth) {
            promises.push(Promise.resolve(this.#updateAuth(json?.auth)).then(() => clearGarbageAuthData(json)));
        }
        if (json.cards && this.#updateCards) {
            promises.push(this.#updateCards(json));
        }
        if (json.messageBodyMessages && this.#updateMessages) {
            promises.push(Promise.resolve(this.#updateMessages(json.messageBodyMessages)));
        }
        if (json.authPromoAction && this.#updateAuthPromoBlock) {
            promises.push(this.#updateAuthPromoBlock(json.authPromoAction));
        }
        if (json.extra && this.#setExtra) {
            promises.push(this.#setExtra(json.extra));
        }
        if ((json.extra?.paymentHistory || json.extra?.billHistory) && this.#setHistory) {
            promises.push(this.#setHistory(json.extra));
        }
        return Promise.all(promises).then(() => json);
    }
}
